import { ScrollText, Waypoints } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { api, isApiError } from '../../../api';
import { TONE_CLASS, TONE_ICON } from '../../../components/Badge';
import { Callout, Section } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { CorrelationLink, DomainLink, PlainId, TraceLink } from '../../../components/Ids';
import { KeyValue } from '../../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../../components/States';
import { formatDateTime } from '../../../lib/format';
import { correlationLogQuery, logsLink, traceSearchLink } from '../../../lib/opsQuery';
import { useApi } from '../../../lib/useApi';
import { TransactionTable } from '../../registry/shared';
import { EnumBadge, humanize, JsonBlock, LinkButton, STEP_TONE, WORKFLOW_TONE } from '../shared';
import { resourceName } from './vocab';

/** `/ops/workflows/:id`: one workflow with its append-only step history and registry commands. */
export function WorkflowPage() {
  const { t } = useTranslation('ops');
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getWorkflow(id, { signal }), [id]);
  const loaded = result.status === 'success' ? result.data.workflow : null;
  const headline = loaded ? [humanize(loaded.workflow_type), resourceName(loaded)].filter(Boolean).join(' · ') : t('workflow.title');
  useDocumentTitle(headline);
  const crumbs = [{ label: t('crumb') }, { label: t('workflow.crumb'), to: '/ops/workflows' }, { label: headline }];

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('workflow.title')} crumbs={crumbs} />
        <Loading rows={4} />
      </>
    );
  }
  if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={t('workflow.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('workflow.notFound') : t('workflow.loadFailed')} />
      </>
    );
  }
  const { workflow: w, steps, registry_transactions: txs } = result.data;
  const stuck = w.state === 'MANUAL_REVIEW' || w.state === 'FAILED';

  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span>{humanize(w.workflow_type)}</span>
            {resourceName(w) ? <DomainLink keyOrName={resourceName(w)} /> : null}
            <EnumBadge group="workflowState" value={w.state} tones={WORKFLOW_TONE} />
          </span>
        }
        crumbs={crumbs}
        subtitle={<CopyText value={w.id} />}
        actions={
          w.correlation_id ? (
            <>
              <LinkButton to={logsLink(correlationLogQuery(w.correlation_id), '7d')}>
                <ScrollText /> {t('workflow.follow.logs')}
              </LinkButton>
              <LinkButton to={traceSearchLink({ correlation_id: w.correlation_id }, '7d')}>
                <Waypoints /> {t('workflow.follow.traces')}
              </LinkButton>
            </>
          ) : undefined
        }
      />
      {stuck && w.failure_message ? (
        <Callout tone={w.state === 'FAILED' ? 'crit' : 'serious'} className="mb-6" title={w.failure_code ?? undefined}>
          {w.failure_message}
        </Callout>
      ) : null}
      <Tabs defaultValue="instance">
        <div className="overflow-x-auto">
          <TabsList>
            <TabsTrigger value="instance">{t('workflow.tab.instance')}</TabsTrigger>
            <TabsTrigger value="steps">{t('workflow.tab.steps', { count: steps.length })}</TabsTrigger>
            <TabsTrigger value="commands">{t('workflow.tab.commands', { count: txs.length })}</TabsTrigger>
            <TabsTrigger value="payload">{t('workflow.tab.payload')}</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="instance" className="space-y-6">
          <Section title={t('workflow.instanceTitle')}>
            <KeyValue
              columns={3}
              rows={[
                [t('workflow.field.state'), <EnumBadge key="s" group="workflowState" value={w.state} tones={WORKFLOW_TONE} />],
                [t('workflow.field.step'), w.current_step ? <code key="c" className="text-xs">{w.current_step}</code> : '—'],
                [t('workflow.field.businessKey'), <CopyText key="b" value={w.business_key} />],
                [t('workflow.field.attempt'), w.attempt],
                [
                  t('workflow.field.resource'),
                  resourceName(w) ? (
                    <DomainLink key="r" keyOrName={resourceName(w)} />
                  ) : w.resource_id ? (
                    <span key="r">
                      {w.resource_type} <PlainId id={w.resource_id} n={36} />
                    </span>
                  ) : (
                    '—'
                  ),
                ],
                [t('workflow.field.nextRun'), formatDateTime(w.next_run_at)],
                [t('workflow.field.lease'), w.locked_by ? t('workflow.field.leaseValue', { worker: w.locked_by, until: formatDateTime(w.locked_until) }) : t('workflow.field.leaseNone')],
                [
                  t('workflow.field.failure'),
                  w.failure_code ? (
                    <span key="f">
                      <code className="text-xs text-critical">{w.failure_code}</code> {w.failure_message}
                    </span>
                  ) : (
                    '—'
                  ),
                ],
                [t('workflow.field.created'), formatDateTime(w.created_at)],
                [t('workflow.field.updated'), formatDateTime(w.updated_at)],
                [t('workflow.field.completed'), formatDateTime(w.completed_at)],
                [t('workflow.field.version'), w.version],
              ]}
            />
          </Section>
          <Section title={t('workflow.follow.title')}>
            <KeyValue
              columns={3}
              rows={[
                [t('workflow.follow.correlation'), <CorrelationLink key="c" id={w.correlation_id} n={36} />],
                [t('workflow.follow.request'), <PlainId key="r" id={w.request_id} n={36} />],
                [t('workflow.follow.causedBy'), <PlainId key="e" id={w.causation_id} n={36} />],
              ]}
            />
          </Section>
        </TabsContent>

        <TabsContent value="steps">
          <Section>
            {steps.length === 0 ? (
              <EmptyState title={t('workflow.steps.empty')} />
            ) : (
              <ol className="relative space-y-5 before:absolute before:top-2 before:bottom-2 before:left-3.5 before:w-px before:bg-border">
                {steps.map((s) => {
                  const tone = STEP_TONE[s.outcome] ?? 'neutral';
                  const Icon = TONE_ICON[tone];
                  return (
                    <li key={s.id} className="relative flex gap-4">
                      <span className={cn('z-10 flex size-7 shrink-0 items-center justify-center rounded-full border bg-card', TONE_CLASS[tone])} aria-hidden="true">
                        {Icon ? <Icon className="size-3.5" /> : <span className="size-2 rounded-full bg-current" />}
                      </span>
                      <div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <code className="text-sm font-semibold">{s.step_name}</code>
                          <EnumBadge group="stepOutcome" value={s.outcome} tones={STEP_TONE} />
                          {s.attempt > 1 ? <span className="text-xs text-muted-foreground">{t('workflow.steps.attempt', { n: s.attempt })}</span> : null}
                          <time dateTime={s.finished_at} className="text-xs text-muted-foreground sm:ml-auto">
                            {formatDateTime(s.finished_at)}
                          </time>
                        </div>
                        {s.error_code || s.error_message ? (
                          <p className="text-sm">
                            {s.error_code ? <code className="text-xs text-critical">{s.error_code}</code> : null} {s.error_message}
                          </p>
                        ) : null}
                        <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                          <span>{t('workflow.steps.started', { at: formatDateTime(s.started_at) })}</span>
                          {s.worker_id ? (
                            <span>
                              · {t('workflow.steps.worker')} <code>{s.worker_id}</code>
                            </span>
                          ) : null}
                          {s.trace_id ? (
                            <span>
                              · <TraceLink id={s.trace_id} label={t('workflow.steps.trace')} />
                            </span>
                          ) : null}
                        </p>
                        {s.output !== null ? <JsonBlock label={t('workflow.steps.output')} value={s.output} /> : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="commands">
          <Section flush>
            {txs.length === 0 ? (
              <div className="px-5 pb-5">
                <EmptyState title={t('workflow.commands.empty')} />
              </div>
            ) : (
              <TransactionTable rows={txs} />
            )}
          </Section>
        </TabsContent>

        <TabsContent value="payload">
          <Section description={t('workflow.payload.note')} title={t('workflow.tab.payload')}>
            <div className="space-y-3">
              <JsonBlock label={t('workflow.payload.input')} value={w.input} open />
              <JsonBlock label={t('workflow.payload.context')} value={w.context} />
            </div>
          </Section>
        </TabsContent>
      </Tabs>
    </>
  );
}
